<template>
  <div>
    <b-row>
      <!-- Top Controls -->
      <b-col
        cols="12"
        class="d-flex align-items-center justify-content-between flex-wrap"
        style="gap: 8px"
      >
        <div class="d-flex flex-wrap" style="gap: 16px">
          <div class="d-flex align-items-center" style="gap: 16px">
            <b-form-input
              size="md"
              placeholder="Search segments..."
              v-model="searchQuery"
            />
            <b-button size="md" variant="outline-secondary" class="d-flex"
              ><feather-icon icon="FilterIcon" /> Filter</b-button
            >
          </div>

          <div>
            <b-button-group size="md">
              <b-button
                v-for="(i, idx) in views"
                :key="idx"
                :variant="i.type === view ? 'dark' : 'outline-dark'"
                @click="toggleView(i.type)"
              >
                <feather-icon :icon="i.icon" />
              </b-button>
            </b-button-group>
          </div>
        </div>
        <b-link :to="{ name: 'marketing-customer-segments-new' }">
          <b-button variant="dark">
            <feather-icon icon="PlusIcon" /> Create New Segment
          </b-button>
        </b-link>
      </b-col>

      <!-- Segment Statistics -->
      <b-col cols="12" class="mt-1">
        <b-row>
          <b-col md="3" v-for="(stat, index) in stats" :key="index">
            <b-card border-variant="secondary" class="text-center shadow-sm">
              <div class="d-flex" style="gap: 16px">
                <div
                  class="d-flex align-items-center justify-content-center rounded-pill"
                  style="background: #fbf9fa; width: 30px; height: 30px"
                >
                  <feather-icon :icon="stat.icon" size="16" />
                </div>
                <div class="justify-content-center align-items-center">
                  <p class="text-muted mb-0">{{ stat.title }}</p>
                  <div class="d-flex align-items-center" style="gap: 8px">
                    <h3>{{ stat.value }}</h3>
                    <small class="text-muted">+{{ stat.delta }}</small>
                  </div>
                </div>
              </div>
              <div class="text-muted small text-left">{{ stat.subtext }}</div>
            </b-card>
          </b-col>
        </b-row>
      </b-col>
    </b-row>

    <b-row class="mt-1" v-if="view === 'list'">
      <b-col cols="12">
        <b-card no-body>
          <b-table
            ref="refSegmantTable"
            :items="fetchSegmentList"
            :fields="tableColumns"
            responsive
            show-empty
            primary-key="id"
            thead-class="text-nowrap"
          >
            <template #empty>
              <div v-if="loadingList" class="text-center text-dark my-2">
                <p>Loading... Please wait</p>
              </div>

              <div
                v-else-if="!loadingList && fetchSegmentList.length === 0"
                class="text-center text-dark my-2"
              >
                No matching records found
              </div>
            </template>

            <template #cell(segmentName)="{ item }">
              <div class="d-flex align-items-center font-weight-bold id-column">
                <span class="text-nowrap">{{ item.name }}</span>
              </div>
            </template>
            <template #cell(description)="{ item }">
              <b-media vertical-align="center">
                <div class="text-muted text-nowrap">
                  {{ item.description }}
                </div>
              </b-media>
            </template>

            <template #cell(clients)="{ item }">
              <b-media vertical-align="center">
                {{ item.clients }}
              </b-media>
            </template>
            <template #cell(status)="{ item }">
              <b-media vertical-align="center">
                <b-badge
                  class="text-capitalize"
                  pill
                  :variant="getStatusBadgeClass(item.status)"
                >
                  {{ item.status }}
                </b-badge>
              </b-media>
            </template>

            <template #cell(criteria)="{ item }">
              <b-media vertical-align="center">
                <div>
                  {{ formatCriteria(item.query).join("; ") }}
                </div>
              </b-media>
            </template>
            <template #cell(lastRefreshed)="{ item }">
              <div class="d-flex align-items-center">
                <feather-icon icon="ClockIcon" size="16" class="align-middle" />
                <div
                  class="ml-1 text-nowrap d-flex align-items-center text-muted"
                >
                  {{
                    formatDistanceStrict(new Date(item.updatedTime), new Date())
                  }}
                  ago
                </div>
              </div>
            </template>
            <template #cell(actions)="{ item }">
              <div v-if="item.id !== 'all-user-verified'" class="text-nowrap">
                <b-dropdown variant="link" toggle-class="p-0">
                  <template #button-content>
                    <feather-icon
                      icon="MoreVerticalIcon"
                      size="16"
                      class="align-middle text-body"
                    />
                  </template>
                  <b-dropdown-item
                    @click="
                      $router.push(`/marketing/segments-customer/${item.id}`)
                    "
                  >
                    <feather-icon class="mr-1" icon="EditIcon" />
                    <span>Edit Segment</span>
                  </b-dropdown-item>
                  <b-dropdown-item @click="showModal('copy', item)">
                    <feather-icon class="mr-1" icon="CopyIcon" />
                    <span>Copy Segment</span>
                  </b-dropdown-item>
                  <b-dropdown-item @click="showModal('pause', item)">
                    <feather-icon class="mr-1" icon="PauseIcon" />
                    <span
                      >{{
                        item.status === "active" ? "Pause" : "Active"
                      }}
                      Segment</span
                    >
                  </b-dropdown-item>
                  <b-dropdown-item @click="showModal('delete', item)">
                    <feather-icon
                      class="mr-1"
                      icon="TrashIcon"
                      style="color: red"
                    />
                    <span class="text-danger">Delete Segment</span>
                  </b-dropdown-item>
                </b-dropdown>
              </div>
            </template>
          </b-table>
        </b-card>
      </b-col>
    </b-row>

    <b-row class="mt-1" v-if="view === 'grid'">
      <b-col
        v-for="(item, idx) in items"
        :key="idx"
        cols="12"
        md="6"
        lg="4"
        class="mb-2"
      >
        <b-card class="h-100">
          <div class="d-flex align-items-center justify-content-between">
            <p
              class="mb-0"
              style="font-weight: 600; color: #0d6efd; font-size: 18px"
            >
              {{ item.name }}
            </p>
            <div v-if="item.id !== 'all-user-verified'" class="text-nowrap">
              <b-dropdown dropleft variant="link" toggle-class="p-0" class="">
                <template #button-content>
                  <feather-icon
                    icon="MoreVerticalIcon"
                    size="16"
                    class="align-middle text-body"
                  />
                </template>
                <b-dropdown-item
                  @click="
                    $router.push(`/marketing/segments-customer/${item.id}`)
                  "
                >
                  <feather-icon class="mr-1" icon="EditIcon" />
                  <span>Edit Segment</span>
                </b-dropdown-item>
                <b-dropdown-item @click="showModal('copy', item)">
                  <feather-icon class="mr-1" icon="CopyIcon" />
                  <span>Copy Segment</span>
                </b-dropdown-item>
                <b-dropdown-item @click="showModal('pause', item)">
                  <feather-icon class="mr-1" icon="PauseIcon" />
                  <span
                    >{{
                      item.status === "active" ? "Pause" : "Active"
                    }}
                    Segment</span
                  >
                </b-dropdown-item>
                <b-dropdown-item @click="showModal('delete', item)">
                  <feather-icon
                    class="mr-1"
                    icon="TrashIcon"
                    style="color: red"
                  />
                  <span class="text-danger">Delete Segment</span>
                </b-dropdown-item>
              </b-dropdown>
            </div>
          </div>
          <span class="text-muted">{{ item.description }}</span>
          <div class="mt-1 d-flex align-items-center">
            <span class="mr-2">{{ item.usersCount }} clients</span>
            <b-badge
              class="text-capitalize"
              pill
              :variant="getStatusBadgeClass(item.status)"
            >
              {{ item.status }}
            </b-badge>
          </div>

          <div class="mt-1">
            <div
              v-for="(i, idx) in formatCriteria(item.query)"
              :key="idx"
              class="bg-light rounded"
              style="padding: 4px; display: inline-block; margin: 2px 2px"
            >
              <span class="text-muted">{{ i }} </span>
            </div>
          </div>
          <div
            class="mt-1 pt-1 d-flex align-items-center"
            style="gap: 8px; border-top: 1px solid #d1d5dc"
          >
            <feather-icon icon="ClockIcon" size="16" class="align-middle" />
            <span class="text-muted">
              Last refreshed:
              {{
                formatDistanceStrict(new Date(item.updatedTime), new Date())
              }}</span
            >
          </div>
        </b-card>
      </b-col>
    </b-row>

    <!-- MODAL CONFIRM -->
    <b-modal
      ref="modal"
      :title="modal.title"
      centered
      cancel-variant="outline-secondary"
      :ok-disabled="isSubmitting"
      @ok="handleSubmitModal"
    >
      <b-card-text class="text-title">
        {{ modal.content }}
      </b-card-text>
      <template #modal-ok>
        <Spinner v-if="isSubmitting" />
        <span v-else>Submit</span>
      </template>
    </b-modal>
  </div>
</template>

<script>
import Spinner from "@core/components/Spinner";
import {
  BButton,
  BCard,
  BCol,
  BPagination,
  BRow,
  BButtonGroup,
  BTable,
  BCardBody,
  BFormInput,
  BMedia,
  BAvatar,
  BLink,
  BDropdown,
  BDropdownItem,
  BTooltip,
  BInputGroupAppend,
  BInputGroup,
  BBadge,
  VBModal,
  BCardText,
} from "bootstrap-vue";
import vSelect from "vue-select";
import flatPickr from "vue-flatpickr-component";
import FeatherIcon from "@/@core/components/feather-icon/FeatherIcon.vue";
import { computed, ref, watch } from "@vue/composition-api";
import { mapState, mapActions } from "vuex";
import debounce from "debounce";
import useToastHandler from "@/services/toastHandler";
import { formatDistanceStrict } from "date-fns";
import { fieldsCustomerSegment } from "@/@core/utils/enum/user";

export default {
  components: {
    BButton,
    BCard,
    BRow,
    BCol,
    BTable,
    BMedia,
    BPagination,
    vSelect,
    Spinner,
    BButtonGroup,
    BTable,
    BCardBody,
    BFormInput,
    BAvatar,
    flatPickr,
    BLink,
    FeatherIcon,
    BDropdown,
    BDropdownItem,
    BTooltip,
    BInputGroupAppend,
    BInputGroup,
    BBadge,
    BCardText,
  },
  directives: {
    "b-modal": VBModal,
  },
  data() {
    return {
      views: [
        {
          type: "grid",
          icon: "GridIcon",
        },
        {
          type: "list",
          icon: "ListIcon",
        },
      ],
      stats: [
        {
          title: "Total Segments",
          value: "18",
          delta: "3",
          icon: "UsersIcon",
          subtext: "vs previous month",
        },
        {
          title: "Active Tags",
          value: "42",
          delta: "8",
          icon: "ShieldIcon",
          subtext: "This month",
        },
        {
          title: "Avg. Segment Size",
          value: "24.5k",
          delta: "12%",
          icon: "UsersIcon",
          subtext: "vs previous month",
        },
        {
          title: "Auto Segments",
          value: "7",
          delta: "2",
          icon: "PlayCircleIcon",
          subtext: "vs previous month",
        },
      ],
      currentPage: 1,
      option: "Last 30 days",
      startDate: "",
      endDate: "",
      options: [
        { label: "Last 30 days", value: "last30days" },
        { label: "Last 7 days", value: "last7days" },
        { label: "Last 90 days", value: "last90days" },
      ],
      items: [],
      tableColumns: [
        { key: "segmentName", label: "Segment Name" },
        { key: "description", label: "Description" },
        { key: "usersCount", label: "Clients" },
        { key: "status", label: "Status" },
        // { key: "accessUsers", label: "Access Users" },
        { key: "criteria", label: "Criteria" },
        { key: "lastRefreshed", label: "Last Refreshed" },

        { key: "actions", label: "Actions" },
      ],
      configDate: {
        // dateFormat: 'd-m-Y',
        altFormat: "d-m-Y",
        altInput: true,
        static: true,
      },
      action: {
        type: "",
        item: "",
      },
      modal: {
        title: "",
        content: "",
      },
    };
  },
  computed: {
    ...mapState("segment/list", [
      "loadingList",
      "loadingDelete",
      "loadingUpsertSegment",
    ]),
    isSubmitting() {
      return this.loadingDelete || this.loadingUpsertSegment;
    },
  },
  async mounted() {},
  watch: {
    view(val) {
      if (val === "grid") {
        this.fetchSegmentList();
      }
    },
    searchQuery(val) {
      if (this.view === "grid") {
        debounce(() => {
          this.fetchSegmentList();
        }, 700)();
      }
    },
  },
  methods: {
    ...mapActions({
      getSegmentsList: "segment/list/getSegmentsList",
      deleteSegment: "segment/list/deleteSegment",
      upsertSegment: "segment/list/upsertSegment",
    }),
    formatCriteria(data) {
      if (!Array.isArray(data)) return;
      let list = [];

      const convertData = (i) => {
        const name = fieldsCustomerSegment.find(
          (seg) => seg.value === i.field
        ).label;
        switch (i.field) {
          case ("isVerified", "hasDepositLimit"):
            return `${name} ${i.operator} ${i.value ? "true" : "false"}`;
          case "lastActive":
            return `${name} ${i.operator} ${i.value} ${
              Number(i.value) > 1 ? "days" : "day"
            } ago`;
          default:
            return `${name} ${i.operator} ${i.value}`;
        }
      };
      data.forEach((item) => {
        item.groups.forEach((group) => {
          group.conditions.forEach((condition) => {
            list.push(convertData(condition));
          });
        });
      });
      return list;
    },
    toggleView(view) {
      this.view = view;
    },
    async fetchSegmentList() {
      const data = await this.getSegmentsList({
        page: this.currentPage,
        limit: this.perPage,
        ...(this.searchQuery && { searchName: this.searchQuery }),
      });
      this.totalSegments = data.pagination.total;
      this.items = data.data;
      if (!data)
        return this.showToastError(false, "Error fetching segment list");
      return data.data;
    },
    getStatusBadgeClass(status) {
      switch (status) {
        case "active":
          return "success";
        case "inactive":
          return "secondary";
        case "paused":
          return "warning";
        default:
          break;
      }
    },
    setModal(title, content) {
      this.modal.title = title;
      this.modal.content = content;
    },

    showModal(type, item) {
      this.action = { item, type };
      if (type === "delete") {
        this.setModal(
          "Delete Segment",
          `Are you sure you want to delete segment ${item.name}?`
        );
      }

      if (type === "pause") {
        this.setModal(
          "Update Status",
          `Are you sure you want to ${
            item.status === "active" ? "pause" : "active"
          } segment name ${item.name}?`
        );
      }

      if (type === "copy") {
        this.setModal(
          "Copy Segment",
          `Are you sure you want to copy segment ${item.name}?`
        );
      }

      this.$refs.modal.show();
    },

    async handleSubmitModal(event) {
      event.preventDefault();
      const { item, type } = this.action;

      if (type === "delete") await this.handleDeleteSegment(item);
      if (type === "pause") await this.handleUpdateStatus(item);
      if (type === "copy") await this.handleCopySegment(item);

      if (!this.isSubmitting) {
        this.$nextTick(() => {
          this.$refs.modal.hide();
        });
      }
    },
    async handleDeleteSegment(item) {
      const res = await this.deleteSegment(item.id);
      if (res) {
        this.showToastSuccess(true, "Segment deleted successfully!");
        this.refetchData();
      }
    },
    async handleUpdateStatus(item) {
      const res = await this.upsertSegment({
        id: item.id,
        status: item.status === "active" ? "paused" : "active",
        name: item.name,
        description: item.description,
        query: item.query,
      });
      if (res) {
        this.showToastSuccess(true, "Segment updated successfully!");
        this.refetchData();
      }
    },
    async handleCopySegment(item) {
      const res = await this.upsertSegment({
        status: item.status,
        name: item.name + " (Copy)",
        description: item.description,
        query: item.query,
      });
      if (res) {
        this.showToastSuccess(true, "Segment updated successfully!");
        this.refetchData();
      }
    },
  },
  setup() {
    const refSegmantTable = ref(null);
    const totalSegments = ref(0);
    const currentPage = ref(1);
    const perPage = ref(100);
    const searchQuery = ref(null);
    const view = ref("list");

    const dataMeta = computed(() => {
      const localItemsCount = refSegmantTable.value
        ? refSegmantTable.value.localItems.length
        : 0;
      return {
        from:
          perPage.value * (currentPage.value - 1) + (localItemsCount ? 1 : 0),
        to: perPage.value * (currentPage.value - 1) + localItemsCount,
        of: totalSegments.value,
      };
    });

    const refetchData = () => {
      refSegmantTable.value.refresh();
    };

    watch([currentPage], () => {
      refetchData();
    });

    watch(
      [searchQuery],
      debounce(() => {
        if (view.value !== "grid") return refetchData();
        // eslint-disable-next-line comma-dangle
      }, 700)
    );

    const { showToastError, showToastSuccess } = useToastHandler();

    return {
      showToastError,
      showToastSuccess,

      refSegmantTable,
      totalSegments,
      currentPage,
      perPage,
      dataMeta,
      refetchData,
      searchQuery,
      formatDistanceStrict,
      view,
    };
  },
};
</script>
<style lang="scss">
@import "@core/scss/vue/libs/vue-select.scss";
@import "@core/scss/vue/libs/vue-flatpicker.scss";
.multi-line-ellipsis {
  display: -webkit-box;
  -webkit-line-clamp: 1;
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-overflow: ellipsis;
}

.text-title {
  color: black;
}
</style>

